import {
  ArrowRightLeft,
  BadgeDollarSign,
  CheckCircle2,
  ClipboardEdit,
  CloudUpload,
  Eraser,
  Images,
  Languages,
  LineChart,
  Link2,
  PackageSearch,
  Puzzle,
  RefreshCw,
  Sparkles,
  Wand2,
} from 'lucide-react';

export const navigation = [
  { label: 'Home', href: '/' },
  { label: 'Features', href: '/features' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'FAQ', href: '/faq' },
  { label: 'Contact', href: '/contact' },
];

export const footerNavigation = [
  ...navigation,
  { label: 'Privacy Policy', href: '/privacy-policy' },
  { label: 'Terms of Service', href: '/terms-of-service' },
];

export const features = [
  {
    icon: PackageSearch,
    title: 'Import by product link',
    description:
      'Paste an AliExpress link to get a Shopify-ready draft. DropBridge reads the product from your connected AliExpress account and, if that can’t return it, from the Chrome extension. If DropBridge can’t read the product properly, it tells you instead of guessing, and you check everything before it reaches your store.',
  },
  {
    icon: Link2,
    title: 'One-click AliExpress connection',
    description:
      'Connect your AliExpress account in one click, right where you need it. DropBridge then reads product details and prices for you, for imports and for Auto Sync.',
  },
  {
    icon: Puzzle,
    title: 'Browser extension',
    description:
      'Send a product to DropBridge for review from the AliExpress page you’re already viewing. Choose the price you see, or the regular price without new-customer deals.',
  },
  {
    icon: ArrowRightLeft,
    title: 'Full variant support',
    description:
      'Import every option and variant — colors, sizes, and more — with per-variant pricing and stock status carried over automatically.',
  },
  {
    icon: Images,
    title: 'Automatic image import',
    description:
      'Product photos and variant-specific images come in together, correctly matched to each variant with no duplicate uploads.',
  },
  {
    icon: ClipboardEdit,
    title: 'Edit before publishing',
    description:
      'Refine titles, descriptions, options, variants, images, and pricing in a review step. Nothing reaches your store until you publish it.',
  },
  {
    icon: Wand2,
    title: 'AI title and description rewriting',
    description:
      'Turn raw supplier copy into clean store-ready listings in one click. Choose the tone, and review every rewrite before it touches your draft — with one-click undo.',
  },
  {
    icon: Eraser,
    title: 'AI background removal',
    description:
      'Remove the background from any product photo, then place it on a white, neutral, or custom colour. Changing the colour afterwards is instant, unlimited, and never uses your allowance.',
  },
  {
    icon: Languages,
    title: 'AI translation',
    description:
      'Rewrite a listing directly into English, Spanish, French, German, Italian, Portuguese, Dutch, or Arabic — or keep the language the supplier used.',
  },
  {
    icon: BadgeDollarSign,
    title: 'Markup controls and AI suggestions',
    description:
      'Apply percentage or fixed markups so margins stay consistent across every variant, and get an AI-suggested markup based on the supplier cost and the product itself.',
  },
  {
    icon: LineChart,
    title: 'Margin-aware catalog building',
    description:
      'Model landed cost, sale price, and markup strategy before committing new products.',
  },
  {
    icon: RefreshCw,
    title: 'Auto Sync for price and stock',
    description:
      'Keep prices and stock in step with AliExpress automatically after you publish. Included on every plan, Free too.',
  },
];

export const steps = [
  {
    icon: PackageSearch,
    title: 'Find products',
    description:
      'Paste a product link once your AliExpress account is connected (one click), or send it straight from AliExpress with the Chrome extension. It arrives as an editable draft, options, variants, and images included.',
  },
  {
    icon: ClipboardEdit,
    title: 'Polish listings',
    description:
      'Refine product text, options, variants, images, pricing, and markup before publishing.',
  },
  {
    icon: CloudUpload,
    title: 'Publish to Shopify',
    description:
      'Publish the reviewed draft with variants, images, and markup rules already applied, then let Auto Sync keep its price and stock up to date.',
  },
];

type PricingPlan = {
  name: string;
  price: string;
  cadence?: string;
  description: string;
  featured: boolean;
  badge: string;
  billingNote?: string;
  features: string[];
};

export const pricingPlans: PricingPlan[] = [
  {
    name: 'Free',
    price: '$0',
    cadence: '/month',
    description:
      'Import products with their variants, images, and descriptions, and try Auto Sync and the AI tools.',
    featured: false,
    badge: 'Start here',
    features: [
      'Up to 10 product imports in total',
      'Import by link after connecting AliExpress in one click',
      'Chrome extension included',
      'Variants, images, and pricing rules included',
      'Edit everything before it reaches your store',
      'Auto Sync up to 10 products',
      '10 "Improve with AI" uses and 10 background removals to try (they don’t renew)',
    ],
  },
  {
    name: 'Pro',
    price: '$18.90',
    cadence: '/month',
    description:
      'For a focused catalog, with unlimited imports and AI every day.',
    featured: true,
    badge: 'Most popular',
    billingNote: '$189.99/year available · 3-day free trial',
    features: [
      'Everything in Free',
      'No product import limit',
      'Auto Sync up to 100 products',
      '75 "Improve with AI" uses and 75 background removals every day',
      'Priority support',
    ],
  },
  {
    name: 'Business',
    price: '$49.90',
    cadence: '/month',
    description:
      'For stores syncing hundreds of products with their supplier.',
    featured: false,
    badge: 'Growing catalogs',
    billingNote: '$479.90/year available',
    features: [
      'Everything in Pro',
      'Auto Sync up to 500 products',
      '300 "Improve with AI" uses and 300 background removals every day',
      'No product import limit',
      'Priority support',
    ],
  },
  {
    name: 'Scale',
    price: '$129.90',
    cadence: '/month',
    description:
      'For large catalogs where supplier prices and stock move constantly.',
    featured: false,
    badge: 'Highest limits',
    billingNote: '$1,249.90/year available',
    features: [
      'Everything in Business',
      'Auto Sync up to 2,000 products',
      '750 "Improve with AI" uses and 750 background removals every day',
      'No product import limit',
      'Priority support and onboarding',
    ],
  },
];

export const faqs = [
  {
    question: 'What does DropBridge do?',
    answer:
      'DropBridge helps Shopify merchants create product listing drafts from merchant-selected supplier details, edit product information, apply price markups, publish cleaner listings to Shopify, and keep supplier prices and stock in sync afterwards with Auto Sync.',
  },
  {
    question: 'Does DropBridge import product variants?',
    answer:
      'Yes. Options like color and size, along with their per-variant pricing and stock status, are imported automatically and can be edited before publishing.',
  },
  {
    question: 'Are product images imported too?',
    answer:
      'Yes. Main product photos and any variant-specific images are imported and matched to the right variant automatically, without creating duplicate images in Shopify.',
  },
  {
    question: 'Do I have to paste a link for every product?',
    answer:
      'No. You can paste a supplier product link, or use the DropBridge browser extension to send a product for review directly from the page you’re viewing.',
  },
  {
    question: 'How does importing by link work?',
    answer:
      'Paste an AliExpress product link. DropBridge reads the product from your connected AliExpress account, with AliExpress’s regular prices. If your account isn’t connected or can’t return that product, the DropBridge Chrome extension reads the page in your browser instead, when it’s installed, and its prices are in the currency you browse AliExpress in. If DropBridge can’t read the product properly, it tells you instead of guessing, and you check everything, prices included, before it reaches your store. AliExpress pages are only ever read in your own browser. On Free, you need to connect your AliExpress account before you can import by link.',
  },
  {
    question: 'What does connecting my AliExpress account do?',
    answer:
      'It lets DropBridge read product details and prices from AliExpress for you: for imports by link, for Auto Sync, and for the regular-price option in the Chrome extension. Connecting takes one click. You approve DropBridge on AliExpress itself, so DropBridge never sees your AliExpress password. The access key AliExpress gives DropBridge is stored encrypted, and you can disconnect at any time, which deletes it.',
  },
  {
    question: 'Which price does the Chrome extension use?',
    answer:
      'You choose, just above the extension’s green button. “Price I see” is the default and sends the price on the page in front of you. “Regular price” leaves out new-customer and welcome deals, so a one-time discount doesn’t end up setting your selling price. It needs your AliExpress account connected in DropBridge. If the regular price isn’t available, the extension sends the price you see and the draft tells you why.',
  },
  {
    question: 'Can I edit products before they appear in Shopify?',
    answer:
      'Yes. DropBridge is designed around a review step, so you can adjust titles, descriptions, options, variants, images, tags, collections, and pricing before publishing.',
  },
  {
    question: 'Does DropBridge support markup rules?',
    answer:
      'Yes. You can apply fixed or percentage-based markup strategies before publishing product drafts to Shopify.',
  },
  {
    question: 'What plans does DropBridge offer?',
    answer:
      'Free costs nothing. It includes up to 10 product imports in total, Auto Sync for up to 10 products, and 10 "Improve with AI" uses plus 10 background removals to try; the imports and AI uses don’t renew. The paid plans all include the same features — unlimited imports, Auto Sync for supplier prices and stock, a daily AI allowance and priority support — and differ in scale. Pro is $18.90 per month or $189.99 per year and syncs up to 100 products, with 75 "Improve with AI" uses and 75 background removals a day. Business is $49.90 per month or $479.90 per year for 500 products and 300 of each a day. Scale is $129.90 per month or $1,249.90 per year for 2,000 products and 750 of each a day, and adds onboarding. Pro includes a 3-day free trial; Business and Scale do not include a trial.',
  },
  {
    question: 'What can the AI features do?',
    answer:
      'DropBridge can rewrite a supplier title and description into clean store-ready copy in the tone you choose, translate it into another language, suggest a markup based on the supplier cost, and remove the background from product photos so you can place them on a white or custom colour. Every AI result is shown to you before it is applied, and rewrites can be undone with one click.',
  },
  {
    question: 'What counts against the AI limit?',
    answer:
      'Each "Improve with AI" rewrite of a title and description counts as one use, including any translation and markup suggestion that comes with it. Each background removal counts as one removal. The two are counted separately, so using one never eats into the other. Changing the background colour of an image you have already processed is free and never counts against your allowance. On Free you get 10 of each, once; they don’t renew. On the paid plans the allowance is daily and resets at midnight UTC.',
  },
];

export const metrics = [
  { value: 'Review', label: 'before publishing' },
  { value: 'Draft-first', label: 'workflow' },
  { value: 'Merchant-controlled', label: 'publishing' },
];

export const socialLinks = [
  { label: 'LinkedIn (to be updated)', href: 'https://linkedin.com' },
  { label: 'Shopify App Store', href: 'https://apps.shopify.com/dropbridge' },
];

export const highlights = [
  'Create product drafts from supplier details',
  'Import variants, options, and images automatically',
  'Rewrite and translate listing copy with AI',
  'Remove image backgrounds and set your own colour',
  'Preview margin impact with markup rules',
  'Publish a cleaner Shopify catalog',
];

export const pageIntros = {
  features: {
    eyebrow: 'Features',
    title: 'Everything you need between supplier research and Shopify.',
    description:
      'DropBridge gives merchants a focused workspace for drafting products — variants, images, and pricing included — improving listings, protecting margins before publishing, and keeping prices and stock in sync afterwards.',
  },
  pricing: {
    eyebrow: 'Pricing',
    title: 'Start free. Upgrade when your catalog grows.',
    description:
      'Free lets you import up to 10 products with their variants, images, and descriptions, keep up to 10 in sync with your supplier, and try the AI tools. The paid plans add unlimited imports and a daily AI allowance, and differ in how many products you sync and how much AI you use.',
  },
  faq: {
    eyebrow: 'FAQ',
    title: 'Answers for merchants evaluating DropBridge.',
    description: 'A practical overview of what the app handles, from imports to publishing.',
  },
};

export const trustItems = [
  { icon: Sparkles, label: 'Premium listing workflow' },
  { icon: CheckCircle2, label: 'Review before publishing' },
  { icon: BadgeDollarSign, label: 'Markup-aware pricing' },
];
